/**
 * Artifact detection — decides whether an assistant message is long/structured
 * enough to be worth lifting OUT of the scrolling transcript and into a
 * dedicated, independently-scrollable side panel (Claude Artifacts / ChatGPT
 * Canvas style). The travel domain produces exactly the content this helps with:
 * a multi-step reissue/reissue-exchange procedure, a long fare-rule extract, a
 * block of GDS/Sabre command lines.
 *
 * DESIGN: a PURE function over the message string so it is trivially unit-tested
 * and memoizable (see ./useArtifact). It NEVER calls into React, the store, or
 * the markdown renderer. The detector only CLASSIFIES + EXTRACTS; rendering is
 * the panel's job.
 *
 * CONSERVATIVE BY DESIGN (the load-bearing decision): a false positive — pulling
 * a short, perfectly-readable answer into a panel — is far more annoying than a
 * false negative. So every threshold is deliberately high, and there is a hard
 * length floor below which NOTHING is ever detected. Short answers stay inline.
 */

/** The two shapes of artifact we lift into the panel. */
export type ArtifactKind = 'code' | 'document';

export type Artifact = {
  kind: ArtifactKind;
  /** A short human title derived from the content (panel header + filename). */
  title: string;
  /** Fence info-string language for a code artifact (e.g. "python"), if any. */
  language?: string;
  /**
   * The text the panel renders. For a code artifact this is the inner source of
   * the dominant fenced block (no fences). For a document it is the whole
   * message (markdown), rendered through the shared <Markdown> component.
   */
  content: string;
};

// ── Thresholds (conservative; see file docstring) ──────────────────────────

/**
 * Nothing shorter than this is EVER an artifact — short answers stay inline.
 * Thresholds below were raised after the 2026-06-20 codex review flagged the
 * first cut as too permissive (medium answers leaking into the panel).
 */
const HARD_FLOOR_CHARS = 500;
/** A fenced code block must clear ONE of these to be "long". */
const CODE_MIN_LINES = 16;
const CODE_MIN_CHARS = 800;
/** A document needs this many numbered top-level steps… */
const DOC_MIN_STEPS = 6;
/** …or this many markdown headings… */
const DOC_MIN_HEADINGS = 6;
/** …AND this much total length, so a short numbered list never panels. */
const DOC_MIN_CHARS = 900;
/**
 * When BOTH a long code block and a long procedure are present, code "wins" only
 * if the code block is the bulk of the message; otherwise the prose around it is
 * the point and we keep it a document.
 */
const CODE_DOMINANCE_RATIO = 0.6;

// ── Fenced-code extraction ─────────────────────────────────────────────────

type Fence = { language?: string; body: string };

/**
 * Pull every ```fenced``` block out of the message. Tolerant of an unterminated
 * trailing fence (mid-stream content) by treating the remainder as the body —
 * but the detector only runs on COMPLETED messages, so that path is defensive.
 */
function extractFences(content: string): Fence[] {
  const fences: Fence[] = [];
  // ``` optionally followed by an info string, then body, then closing ```.
  const re = /```([^\n`]*)\n([\s\S]*?)```/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const info = (m[1] ?? '').trim();
    fences.push({
      language: info ? info.split(/\s+/)[0].toLowerCase() : undefined,
      body: m[2] ?? '',
    });
  }
  return fences;
}

/** Count non-empty lines in a block (the line floor ignores blank padding). */
function nonEmptyLineCount(text: string): number {
  return text.split('\n').filter((l) => l.trim().length > 0).length;
}

/** Strip fenced blocks so prose-structure checks don't see code as "steps". */
function stripFences(content: string): string {
  return content.replace(/```[^\n`]*\n[\s\S]*?```/g, '');
}

// ── Document-structure signals ─────────────────────────────────────────────

/** Top-level numbered steps: lines like "1. ", "2) " at low indentation. */
function numberedStepCount(text: string): number {
  let count = 0;
  for (const line of text.split('\n')) {
    if (/^\s{0,3}\d+[.)]\s+\S/.test(line)) count += 1;
  }
  return count;
}

/** Markdown ATX headings (# … ######). */
function headingCount(text: string): number {
  let count = 0;
  for (const line of text.split('\n')) {
    if (/^\s{0,3}#{1,6}\s+\S/.test(line)) count += 1;
  }
  return count;
}

// ── Title derivation ───────────────────────────────────────────────────────

const TITLE_MAX = 60;

/** First heading, else first non-empty line, truncated; fallback by kind. */
function deriveTitle(content: string, kind: ArtifactKind): string {
  for (const line of content.split('\n')) {
    const heading = line.match(/^\s{0,3}#{1,6}\s+(.*\S)/);
    if (heading) return truncate(heading[1]);
  }
  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    // Skip a leading fence line when titling a code artifact.
    if (trimmed && !trimmed.startsWith('```')) return truncate(trimmed);
  }
  return kind === 'code' ? 'Code' : 'Document';
}

function truncate(s: string): string {
  const clean = s.replace(/[#*`>]/g, '').trim();
  return clean.length > TITLE_MAX ? `${clean.slice(0, TITLE_MAX - 1)}…` : clean;
}

// ── The detector ───────────────────────────────────────────────────────────

/**
 * Classify a completed assistant message. Returns the dominant artifact, or
 * `null` when the message is short/unstructured enough to read inline.
 *
 * PURE + deterministic: same input → same output, no side effects. Safe to
 * memoize on the content string (see useArtifact).
 */
export function detectArtifact(content: string): Artifact | null {
  if (!content || content.length < HARD_FLOOR_CHARS) return null;

  // 1) Longest qualifying fenced code block.
  const fences = extractFences(content);
  let dominantCode: Fence | null = null;
  for (const f of fences) {
    const longEnough =
      nonEmptyLineCount(f.body) >= CODE_MIN_LINES ||
      f.body.length >= CODE_MIN_CHARS;
    if (!longEnough) continue;
    if (!dominantCode || f.body.length > dominantCode.body.length) {
      dominantCode = f;
    }
  }

  // 2) Long structured procedure (measured on the prose, fences removed).
  const prose = stripFences(content);
  const hasStructure =
    numberedStepCount(prose) >= DOC_MIN_STEPS ||
    headingCount(content) >= DOC_MIN_HEADINGS;
  const isDocument = hasStructure && content.length >= DOC_MIN_CHARS;

  // 3) Resolve when both qualify: code wins only if it's the bulk of the message.
  if (dominantCode && isDocument) {
    const codeShare = dominantCode.body.length / content.length;
    if (codeShare < CODE_DOMINANCE_RATIO) {
      return {
        kind: 'document',
        title: deriveTitle(content, 'document'),
        content,
      };
    }
  }

  if (dominantCode) {
    return {
      kind: 'code',
      title: deriveTitle(dominantCode.body, 'code'),
      language: dominantCode.language,
      content: dominantCode.body.replace(/\n+$/, ''),
    };
  }

  if (isDocument) {
    return {
      kind: 'document',
      title: deriveTitle(content, 'document'),
      content,
    };
  }

  return null;
}
